/**
 * Thin fetch wrapper for the RF-CRM REST API.
 *
 * Backend serves SPA + API from the same origin in production. In dev,
 * Vite proxies /api to localhost:3000 (see vite.config.ts).
 *
 * JWT bearer token is injected from sessionStorage. Login persists the
 * token via setAuthToken(); logout / 401 clears it.
 */

const AUTH_STORAGE_KEY = 'rf_crm_token';

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(status: number, message: string, data: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

export function getAuthToken(): string | null {
  return sessionStorage.getItem(AUTH_STORAGE_KEY);
}

export function setAuthToken(token: string | null): void {
  if (token === null) {
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
  } else {
    sessionStorage.setItem(AUTH_STORAGE_KEY, token);
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = path.startsWith('/api') ? path : `/api${path.startsWith('/') ? '' : '/'}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

export async function apiRequest<T = unknown>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, query, signal } = opts;

  const headers: Record<string, string> = {
    'Accept': 'application/json',
  };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(buildUrl(path, query), {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  // Handle empty responses (204, 205, or empty body)
  const contentType = res.headers.get('content-type') ?? '';
  let data: unknown = null;
  if (contentType.includes('application/json')) {
    data = await res.json().catch(() => null);
  } else if (res.status !== 204 && res.status !== 205) {
    data = await res.text().catch(() => null);
  }

  if (!res.ok) {
    const message =
      (data && typeof data === 'object' && 'message' in data && typeof data.message === 'string'
        ? data.message
        : null) ?? res.statusText ?? `HTTP ${res.status}`;

    if (res.status === 401) {
      // Token expired/invalid — clear it. AuthProvider will react on next render.
      setAuthToken(null);
    }
    throw new ApiError(res.status, message, data);
  }

  return data as T;
}

/** Convenience helpers */
export const api = {
  get: <T = unknown>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...opts, method: 'GET' }),
  post: <T = unknown>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...opts, method: 'POST', body }),
  put: <T = unknown>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...opts, method: 'PUT', body }),
  patch: <T = unknown>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T = unknown>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...opts, method: 'DELETE' }),
};

/**
 * Multipart upload (issue #27 attachments). Sends FormData as-is — the
 * browser sets the multipart boundary; do NOT set Content-Type manually.
 */
export async function apiUpload<T = unknown>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(buildUrl(path), { method: 'POST', headers, body: form });
  const data: unknown = await res.json().catch(() => null);

  if (!res.ok) {
    const message =
      (data && typeof data === 'object' && 'message' in data && typeof data.message === 'string'
        ? data.message
        : null) ?? res.statusText ?? `HTTP ${res.status}`;
    if (res.status === 401) setAuthToken(null);
    throw new ApiError(res.status, message, data);
  }
  return data as T;
}

/**
 * Authenticated file download (issue #27). Returns the blob plus the
 * server-provided filename so the caller can trigger a browser save.
 */
export async function apiDownload(path: string): Promise<{ blob: Blob; filename: string }> {
  const headers: Record<string, string> = {};
  const token = getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(buildUrl(path), { headers });
  if (!res.ok) {
    if (res.status === 401) setAuthToken(null);
    throw new ApiError(res.status, res.statusText || `HTTP ${res.status}`);
  }

  const disposition = res.headers.get('content-disposition') ?? '';
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  const plain = /filename="([^"]+)"/i.exec(disposition);
  const filename = star
    ? decodeURIComponent(star[1])
    : plain
      ? plain[1]
      : 'download';

  return { blob: await res.blob(), filename };
}
